// PatientFeedback.js
// =============================================================================
// Surveys > Feedback — full-screen module opened from the Head Office
// dashboard's new "Surveys" row (see HeadOffice.js: DASHBOARD_GROUPS +
// SurveysCard + ComplianceFullPage's `patientFeedback` case).
//
// Self-contained: fetches its own token from localStorage, talks to its own
// backend module (feedback_routes.js), and has no dependency on any other
// component in this repo.
//
// Four tabs, right to left as designed:
//   Poster design    — A4 wall poster: pharmacy name, three resizable text
//                       blocks and a QR code, each positioned/coloured/sized.
//                       Preview renders a real PDF; Cascade emails one PDF
//                       per branch (branch name + branch-specific QR) to
//                       that branch's contact address.
//   Webpage design    — the page the QR opens: position the pharmacy name,
//                       toggle rating / comments / complaint, live preview.
//   Branches           — grid of branches x response counts over a date
//                       range, with an Excel export.
//   Inbox              — incoming feedback not yet reviewed (plus a
//                       Reviewed / All view). Add a note, mark reviewed,
//                       reply by email (routes further correspondence to
//                       the manager's own configured address, not the app).
//
// The public-facing page a customer's QR scan opens lives in
// PatientFeedbackPublic.js, rendered by App.js's "?pfb=TOKEN" early return.
// =============================================================================

import React, { useState, useEffect, useRef, useCallback } from "react";
import * as XLSX from "xlsx";

const API_BASE = "https://api.pharmacegroup.co.uk";
const token = () => localStorage.getItem("token") || localStorage.getItem("authToken") || "";

async function apiPost(path, body) {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token()}` },
    body: JSON.stringify(body || {})
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  return data;
}

async function apiPostBlob(path, body) {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token()}` },
    body: JSON.stringify(body || {})
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || `Request failed (${res.status})`);
  }
  return res.blob();
}

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

// Mirrors feedback_routes.js's DEFAULT_POSTER / DEFAULT_WEBPAGE exactly —
// kept here too so "Restore defaults" is instant and works offline-first,
// without a round trip. If the backend defaults ever change, update both.
const DEFAULT_POSTER = {
  pageBg: "#ffffff",
  pharmacyName: { x: 8, y: 6, w: 84, h: 12, fontSize: 30, bg: "transparent", fg: "#111827" },
  textBlocks: [
    { id: "t1", x: 8, y: 22, w: 84, h: 14, fontSize: 22, bg: "transparent", fg: "#7c3aed",
      text: "We'd love to hear from you" },
    { id: "t2", x: 8, y: 40, w: 54, h: 34, fontSize: 13.5, bg: "transparent", fg: "#374151",
      text: "Scan the code with your phone camera to rate your visit, leave a comment, or raise a complaint. It takes less than a minute, and helps us keep improving your pharmacy." },
    { id: "t3", x: 8, y: 88, w: 84, h: 8, fontSize: 10.5, bg: "transparent", fg: "#9ca3af",
      text: "Thank you for taking the time to help us improve." }
  ],
  qr: { x: 64, y: 40, w: 28, h: 28 }
};

const DEFAULT_WEBPAGE = {
  pharmacyName: { x: 50, y: 8, align: "center", fontSize: 22, fg: "#111827" },
  introText: "Tell us about your visit today",
  showRating: true,
  showComments: true,
  showComplaint: true,
  ratingHelp: "Tap a star to rate your visit, from 1 (poor) to 5 (excellent).",
  commentsHelp: "Tell us what went well, or what we could do better — every comment is read by the branch manager.",
  complaintHelp: "Tick this if you'd like this treated as a formal complaint. We aim to respond within 5 working days."
};

const cloneDefault = (obj) => JSON.parse(JSON.stringify(obj));

// ---------------------------------------------------------------------------
// styles
// ---------------------------------------------------------------------------
const S = {
  wrap: { padding: 16, fontFamily: "system-ui, sans-serif" },
  h1: { fontSize: 20, fontWeight: 700, margin: "0 0 4px", color: "#111827" },
  sub: { fontSize: 13, color: "#6b7280", margin: "0 0 14px" },
  tabs: { display: "flex", gap: 6, marginBottom: 16, borderBottom: "1px solid #e5e7eb", flexWrap: "wrap" },
  tabBtn: (active) => ({
    padding: "8px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer",
    border: "none", borderBottom: active ? "3px solid #7c3aed" : "3px solid transparent",
    background: "transparent", color: active ? "#111827" : "#6b7280"
  }),
  panelRow: { display: "flex", gap: 20, alignItems: "flex-start", flexWrap: "wrap" },
  card: { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: 14 },
  label: { fontSize: 11.5, fontWeight: 700, color: "#374151", textTransform: "uppercase",
           letterSpacing: 0.3, display: "block", margin: "10px 0 4px" },
  input: { width: "100%", boxSizing: "border-box", padding: "7px 9px", fontSize: 13,
           border: "1px solid #d1d5db", borderRadius: 6, fontFamily: "inherit" },
  textarea: { width: "100%", boxSizing: "border-box", padding: "7px 9px", fontSize: 13, minHeight: 60,
              border: "1px solid #d1d5db", borderRadius: 6, fontFamily: "inherit", resize: "vertical" },
  btn: { padding: "8px 14px", fontSize: 13, fontWeight: 600, border: "none", borderRadius: 6,
         background: "#7c3aed", color: "#fff", cursor: "pointer" },
  btnGhost: { padding: "8px 14px", fontSize: 13, fontWeight: 600, borderRadius: 6, cursor: "pointer",
              background: "#fff", color: "#374151", border: "1px solid #d1d5db" },
  btnDanger: { padding: "8px 14px", fontSize: 13, fontWeight: 600, borderRadius: 6, cursor: "pointer",
               background: "#dc2626", color: "#fff", border: "none" },
  err: { fontSize: 12.5, color: "#991b1b", background: "#fee2e2", border: "1px solid #fecaca",
         borderRadius: 6, padding: "7px 10px", marginTop: 10 },
  ok: { fontSize: 12.5, color: "#166534", background: "#dcfce7", border: "1px solid #86efac",
        borderRadius: 6, padding: "7px 10px", marginTop: 10 },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: { textAlign: "left", padding: "8px 10px", borderBottom: "2px solid #e5e7eb", fontSize: 11.5,
        textTransform: "uppercase", letterSpacing: 0.3, color: "#6b7280" },
  td: { padding: "8px 10px", borderBottom: "1px solid #f1f5f9", verticalAlign: "top" }
};

// ---------------------------------------------------------------------------
// Draggable / resizable design block — percentage coordinates (0-100) of a
// fixed-aspect canvas, so the layout survives print at any DPI. Click
// selects (for the properties panel); drag on the body moves; drag on the
// small corner handle resizes.
// ---------------------------------------------------------------------------
function DesignBlock({ block, canvasSize, onChange, onSelect, selected, resizable = true, minW = 6, minH = 4, children }) {
  const dragRef = useRef(null);

  useEffect(() => {
    function onMove(e) {
      const d = dragRef.current;
      if (!d) return;
      const dxPct = ((e.clientX - d.startX) / canvasSize.w) * 100;
      const dyPct = ((e.clientY - d.startY) / canvasSize.h) * 100;
      if (d.mode === "move") {
        const x = clamp(d.start.x + dxPct, 0, 100 - d.start.w);
        const y = clamp(d.start.y + dyPct, 0, 100 - d.start.h);
        onChange({ ...d.start, x, y });
      } else {
        const w = clamp(d.start.w + dxPct, minW, 100 - d.start.x);
        const h = clamp(d.start.h + dyPct, minH, 100 - d.start.y);
        onChange({ ...d.start, w, h });
      }
    }
    function onUp() { dragRef.current = null; }
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [canvasSize, onChange, minW, minH]);

  return (
    <div
      onMouseDown={(e) => { e.stopPropagation(); onSelect && onSelect(); dragRef.current = { mode: "move", startX: e.clientX, startY: e.clientY, start: block }; }}
      style={{
        position: "absolute",
        left: `${block.x}%`, top: `${block.y}%`, width: `${block.w}%`, height: `${block.h}%`,
        cursor: "move", boxSizing: "border-box",
        outline: selected ? "2px dashed #7c3aed" : "1px dashed #cbd5e1",
        outlineOffset: 2
      }}
    >
      {children}
      {resizable && (
        <div
          onMouseDown={(e) => { e.stopPropagation(); onSelect && onSelect(); dragRef.current = { mode: "resize", startX: e.clientX, startY: e.clientY, start: block }; }}
          style={{ position: "absolute", right: -5, bottom: -5, width: 12, height: 12,
                   background: "#7c3aed", borderRadius: 3, cursor: "nwse-resize", zIndex: 5 }}
        />
      )}
    </div>
  );
}

function ColorField({ label, value, onChange, allowTransparent }) {
  const isTransparent = value === "transparent" || !value;
  return (
    <div>
      <label style={S.label}>{label}</label>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <input type="color" value={isTransparent ? "#ffffff" : value}
               onChange={(e) => onChange(e.target.value)}
               disabled={allowTransparent && isTransparent}
               style={{ width: 40, height: 30, padding: 0, border: "1px solid #d1d5db", borderRadius: 4 }} />
        {allowTransparent && (
          <label style={{ fontSize: 12, color: "#374151", display: "flex", alignItems: "center", gap: 4 }}>
            <input type="checkbox" checked={isTransparent}
                   onChange={(e) => onChange(e.target.checked ? "transparent" : "#ffffff")} />
            None
          </label>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// POSTER DESIGN TAB
// =============================================================================
const POSTER_CANVAS = { w: 380, h: 538 }; // ~A4 ratio (1 : 1.4142)

function RecipientsPanel() {
  const [branches, setBranches] = useState(null);
  const [err, setErr] = useState(null);
  const [savingFor, setSavingFor] = useState(null);

  const load = useCallback(async () => {
    setErr(null);
    try {
      const r = await apiPost("/api/feedback/branches", {});
      setBranches(r.branches);
    } catch (e) { setErr(e.message); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const choose = async (dbName, staffId) => {
    setSavingFor(dbName);
    setBranches((rows) => rows.map((b) => {
      if (b.dbName !== dbName) return b;
      const m = (b.managers || []).find((x) => String(x.staffId) === String(staffId));
      return { ...b, recipientStaffId: m ? m.staffId : "", recipientName: m ? m.name : "", recipientEmail: m ? m.email : "" };
    }));
    try {
      await apiPost("/api/feedback/branches/recipient", { dbName, staffId: staffId || null });
    } catch (e) { setErr(e.message); }
    setSavingFor(null);
  };

  return (
    <div style={{ ...S.card, marginTop: 16 }}>
      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>Who gets each branch's poster</div>
      <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 10 }}>
        Same picker as the Action Centre — only active managers/pharmacists with an email on file are listed.
        Leave a branch blank to skip it when you cascade.
      </div>
      {err && <div style={S.err}>{err}</div>}
      {!branches && !err && <div style={{ color: "#6b7280", fontSize: 13 }}>Loading…</div>}
      {branches && (
        <div style={{ overflowX: "auto" }}>
          <table style={S.table}>
            <thead>
              <tr><th style={S.th}>Branch</th><th style={S.th}>Recipient</th></tr>
            </thead>
            <tbody>
              {branches.map((b) => (
                <tr key={b.dbName}>
                  <td style={S.td}>{b.pharmacyName}</td>
                  <td style={S.td}>
                    <select style={{ ...S.input, width: "auto", minWidth: 220 }}
                            value={b.recipientStaffId || ""}
                            disabled={savingFor === b.dbName}
                            onChange={(e) => choose(b.dbName, e.target.value)}>
                      <option value="">
                        {(b.managers || []).length ? "— Select recipient —" : "— No manager/pharmacist with email —"}
                      </option>
                      {(b.managers || []).map((m) => (
                        <option key={m.staffId} value={m.staffId}>{m.name} ({m.roleName}) — {m.email}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
              {branches.length === 0 && <tr><td style={S.td} colSpan={2}>No branches found.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function PosterDesignTab({ poster, setPoster, saving, onSave, message }) {
  const [selected, setSelected] = useState("pharmacyName"); // 'pharmacyName' | 'qr' | 't1'|'t2'|'t3'
  const [previewName, setPreviewName] = useState("Sample Pharmacy");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [cascadeBusy, setCascadeBusy] = useState(false);
  const [cascadeResult, setCascadeResult] = useState(null);
  const [localMsg, setLocalMsg] = useState(null);

  const updateBlock = useCallback((key, next) => {
    setPoster((p) => {
      if (key === "pharmacyName") return { ...p, pharmacyName: next };
      if (key === "qr") return { ...p, qr: next };
      return { ...p, textBlocks: p.textBlocks.map((b) => (b.id === key ? next : b)) };
    });
  }, [setPoster]);

  const selectedBlock = selected === "pharmacyName" ? poster.pharmacyName
    : selected === "qr" ? poster.qr
    : poster.textBlocks.find((b) => b.id === selected);

  const doPreview = async () => {
    setPreviewLoading(true); setLocalMsg(null);
    try {
      const blob = await apiPostBlob("/api/feedback/poster/preview", { pharmacyName: previewName || "Sample Pharmacy" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch (e) { setLocalMsg({ type: "err", text: e.message }); }
    setPreviewLoading(false);
  };

  const doCascade = async () => {
    if (!window.confirm("This will render a poster for every branch and email it to the recipient chosen below for that branch. Continue?")) return;
    setCascadeBusy(true); setCascadeResult(null); setLocalMsg(null);
    try {
      const r = await apiPost("/api/feedback/poster/cascade", {});
      setCascadeResult(r);
    } catch (e) { setLocalMsg({ type: "err", text: e.message }); }
    setCascadeBusy(false);
  };

  const restoreDefaults = () => {
    if (!window.confirm("Reset the poster's text, colours, fonts and layout back to the original defaults? This can't be undone unless you saved a version you want to keep.")) return;
    setPoster(cloneDefault(DEFAULT_POSTER));
    setSelected("pharmacyName");
    setLocalMsg({ type: "ok", text: "Restored to defaults — click Save design to keep this." });
  };

  return (
    <>
    <div style={S.panelRow}>
      <div style={{ ...S.card, width: POSTER_CANVAS.w + 28 }}>
        <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 8 }}>
          Drag a block to move it, drag the purple corner to resize. Click a block to edit it on the right.
        </div>
        <div
          onMouseDown={() => setSelected(null)}
          style={{
            position: "relative", width: POSTER_CANVAS.w, height: POSTER_CANVAS.h,
            background: poster.pageBg === "transparent" ? "#fff" : poster.pageBg,
            border: "1px solid #d1d5db", margin: "0 auto", overflow: "hidden"
          }}
        >
          <DesignBlock block={poster.pharmacyName} canvasSize={POSTER_CANVAS}
                       onChange={(b) => updateBlock("pharmacyName", b)}
                       onSelect={() => setSelected("pharmacyName")} selected={selected === "pharmacyName"}>
            <div style={{
              width: "100%", height: "100%", display: "flex", alignItems: "center",
              background: poster.pharmacyName.bg === "transparent" ? "transparent" : poster.pharmacyName.bg,
              color: poster.pharmacyName.fg, fontSize: poster.pharmacyName.fontSize, fontWeight: 700,
              padding: 4, overflow: "hidden"
            }}>
              {previewName || "Pharmacy Name"}
            </div>
          </DesignBlock>

          {poster.textBlocks.map((b) => (
            <DesignBlock key={b.id} block={b} canvasSize={POSTER_CANVAS}
                         onChange={(nb) => updateBlock(b.id, nb)}
                         onSelect={() => setSelected(b.id)} selected={selected === b.id}>
              <div style={{
                width: "100%", height: "100%",
                background: b.bg === "transparent" ? "transparent" : b.bg,
                color: b.fg, fontSize: b.fontSize, padding: 4, overflow: "hidden", whiteSpace: "pre-wrap"
              }}>
                {b.text}
              </div>
            </DesignBlock>
          ))}

          <DesignBlock block={poster.qr} canvasSize={POSTER_CANVAS}
                       onChange={(b) => updateBlock("qr", b)}
                       onSelect={() => setSelected("qr")} selected={selected === "qr"}>
            <div style={{
              width: "100%", height: "100%", border: "1px dashed #9ca3af",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 11, color: "#6b7280", background: "#f9fafb"
            }}>
              QR code
            </div>
          </DesignBlock>
        </div>
      </div>

      <div style={{ ...S.card, minWidth: 260, flex: 1 }}>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>
          {selected === "pharmacyName" ? "Pharmacy name" : selected === "qr" ? "QR code" : "Text block"}
        </div>

        {selected === "pharmacyName" && (
          <>
            <label style={S.label}>Font size</label>
            <input type="range" min="10" max="60" value={poster.pharmacyName.fontSize}
                   onChange={(e) => updateBlock("pharmacyName", { ...poster.pharmacyName, fontSize: Number(e.target.value) })}
                   style={{ width: "100%" }} />
            <div style={{ display: "flex", gap: 12, marginTop: 6 }}>
              <ColorField label="Text colour" value={poster.pharmacyName.fg}
                          onChange={(v) => updateBlock("pharmacyName", { ...poster.pharmacyName, fg: v })} />
              <ColorField label="Background" allowTransparent value={poster.pharmacyName.bg}
                          onChange={(v) => updateBlock("pharmacyName", { ...poster.pharmacyName, bg: v })} />
            </div>
          </>
        )}

        {selected && selected !== "pharmacyName" && selected !== "qr" && (
          <>
            <label style={S.label}>Text</label>
            <textarea style={S.textarea} value={selectedBlock.text}
                      onChange={(e) => updateBlock(selected, { ...selectedBlock, text: e.target.value })} />
            <label style={S.label}>Font size</label>
            <input type="range" min="8" max="40" value={selectedBlock.fontSize}
                   onChange={(e) => updateBlock(selected, { ...selectedBlock, fontSize: Number(e.target.value) })}
                   style={{ width: "100%" }} />
            <div style={{ display: "flex", gap: 12, marginTop: 6 }}>
              <ColorField label="Text colour" value={selectedBlock.fg}
                          onChange={(v) => updateBlock(selected, { ...selectedBlock, fg: v })} />
              <ColorField label="Background" allowTransparent value={selectedBlock.bg}
                          onChange={(v) => updateBlock(selected, { ...selectedBlock, bg: v })} />
            </div>
          </>
        )}

        {selected === "qr" && (
          <div style={{ fontSize: 12.5, color: "#6b7280", marginTop: 4 }}>
            Position and size only — each branch gets its own code automatically when you cascade.
          </div>
        )}

        <label style={S.label}>Poster background</label>
        <ColorField label="" value={poster.pageBg} onChange={(v) => setPoster((p) => ({ ...p, pageBg: v }))} />

        <div style={{ borderTop: "1px solid #e5e7eb", margin: "16px 0 12px" }} />

        <label style={S.label}>Preview pharmacy name</label>
        <input style={S.input} value={previewName} onChange={(e) => setPreviewName(e.target.value)} />

        <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
          <button style={S.btn} onClick={onSave} disabled={saving}>{saving ? "Saving…" : "Save design"}</button>
          <button style={S.btnGhost} onClick={doPreview} disabled={previewLoading}>{previewLoading ? "Rendering…" : "Preview PDF"}</button>
          <button style={S.btnDanger} onClick={doCascade} disabled={cascadeBusy}>{cascadeBusy ? "Sending…" : "Cascade to all branches"}</button>
        </div>
        <div style={{ marginTop: 8 }}>
          <button style={S.btnGhost} onClick={restoreDefaults}>Restore defaults</button>
        </div>

        {message && <div style={message.type === "err" ? S.err : S.ok}>{message.text}</div>}
        {localMsg && <div style={localMsg.type === "err" ? S.err : S.ok}>{localMsg.text}</div>}

        {cascadeResult && (
          <div style={{ marginTop: 12 }}>
            <div style={S.ok}>Sent to {cascadeResult.sent} branch{cascadeResult.sent === 1 ? "" : "es"}
              {cascadeResult.failed ? `, ${cascadeResult.failed} could not be sent` : ""}.</div>
            {cascadeResult.results.filter(r => !r.sent).length > 0 && (
              <ul style={{ fontSize: 12, color: "#991b1b", marginTop: 6, paddingLeft: 18 }}>
                {cascadeResult.results.filter(r => !r.sent).map((r, i) => (
                  <li key={i}>{r.pharmacyName || r.dbName}: {r.reason}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
    </>
  );
}

// =============================================================================
// WEBPAGE DESIGN TAB (+ shared preview renderer)
// =============================================================================
const WEB_CANVAS = { w: 340, h: 140 }; // just the header strip where the name can move

function StarRating({ value, onChange, readOnly }) {
  return (
    <div style={{ display: "flex", gap: 4 }}>
      {[1, 2, 3, 4, 5].map((n) => (
        <span key={n} onClick={() => !readOnly && onChange(n)}
              style={{ fontSize: 26, cursor: readOnly ? "default" : "pointer", color: n <= value ? "#f59e0b" : "#d1d5db" }}>
          ★
        </span>
      ))}
    </div>
  );
}

// Rendered both by the in-editor "Preview" modal and (independently, in its
// own file) by the real public page — kept intentionally simple/duplicated
// rather than shared, so the public page has zero dependency on this module.
function FeedbackFormPreview({ webpage, pharmacyName, interactive, onSubmit, submitting, submitted, submitError }) {
  const [rating, setRating] = useState(0);
  const [comments, setComments] = useState("");
  const [isComplaint, setIsComplaint] = useState(false);
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");

  const align = webpage.pharmacyName.align || "center";

  return (
    <div style={{ maxWidth: 420, margin: "0 auto", fontFamily: "system-ui, sans-serif" }}>
      <div style={{ textAlign: align, fontSize: webpage.pharmacyName.fontSize, color: webpage.pharmacyName.fg, fontWeight: 700, marginBottom: 14 }}>
        {pharmacyName || "Pharmacy name"}
      </div>
      <div style={{ fontSize: 15, color: "#374151", marginBottom: 18, textAlign: "center" }}>{webpage.introText}</div>

      {webpage.showRating && (
        <div style={{ marginBottom: 18, textAlign: "center" }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: "#6b7280", marginBottom: 4 }}>HOW WAS YOUR VISIT?</div>
          {webpage.ratingHelp && <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 8 }}>{webpage.ratingHelp}</div>}
          <div style={{ display: "flex", justifyContent: "center" }}>
            <StarRating value={rating} onChange={setRating} readOnly={!interactive} />
          </div>
        </div>
      )}

      {webpage.showComments && (
        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 12.5, fontWeight: 700, color: "#6b7280", display: "block", marginBottom: 2 }}>
            FEEDBACK OR COMMENTS
          </label>
          {webpage.commentsHelp && <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 6 }}>{webpage.commentsHelp}</div>}
          <textarea style={{ ...S.textarea, minHeight: 90 }} value={comments} readOnly={!interactive}
                    onChange={(e) => setComments(e.target.value)} placeholder="Tell us what you think…" />
        </div>
      )}

      {webpage.showComplaint && (
        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 13, color: "#374151", display: "flex", alignItems: "center", gap: 8 }}>
            <input type="checkbox" checked={isComplaint} disabled={!interactive} onChange={(e) => setIsComplaint(e.target.checked)} />
            This is a complaint
          </label>
          {webpage.complaintHelp && <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 4, marginLeft: 24 }}>{webpage.complaintHelp}</div>}
        </div>
      )}

      {interactive && (
        <>
          <label style={S.label}>
            Your name {isComplaint ? <span style={{ color: "#b91c1c" }}>(required for a complaint)</span> : "(optional)"}
          </label>
          <input style={S.input} value={customerName} onChange={(e) => setCustomerName(e.target.value)} required={isComplaint} />
          <label style={S.label}>
            Your email {isComplaint
              ? <span style={{ color: "#b91c1c" }}>(required for a complaint, so we can respond)</span>
              : "(optional — only needed if you'd like a reply)"}
          </label>
          <input style={S.input} value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} required={isComplaint} />
        </>
      )}

      {interactive ? (
        submitted ? (
          <div style={{ ...S.ok, marginTop: 16, textAlign: "center" }}>Thank you — your feedback has been sent.</div>
        ) : (
          <>
            <button style={{ ...S.btn, width: "100%", marginTop: 16, padding: "11px 0" }} disabled={submitting}
                    onClick={() => onSubmit({ rating, comments, isComplaint, customerName, customerEmail })}>
              {submitting ? "Sending…" : "Send"}
            </button>
            {submitError && <div style={S.err}>{submitError}</div>}
          </>
        )
      ) : (
        <button style={{ ...S.btn, width: "100%", marginTop: 16, padding: "11px 0", opacity: 0.6, cursor: "default" }}>Send</button>
      )}
    </div>
  );
}

function WebpageDesignTab({ webpage, setWebpage, saving, onSave, message }) {
  const [showPreview, setShowPreview] = useState(false);
  const [localMsg, setLocalMsg] = useState(null);

  const restoreDefaults = () => {
    if (!window.confirm("Reset the webpage's text, colours, fonts and shown sections back to the original defaults? This can't be undone unless you saved a version you want to keep.")) return;
    setWebpage(cloneDefault(DEFAULT_WEBPAGE));
    setLocalMsg({ type: "ok", text: "Restored to defaults — click Save design to keep this." });
  };

  return (
    <div style={S.panelRow}>
      <div style={{ ...S.card, width: WEB_CANVAS.w + 28 }}>
        <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 8 }}>
          Drag the pharmacy name to where it should sit at the top of the page.
        </div>
        <div
          style={{ position: "relative", width: WEB_CANVAS.w, height: WEB_CANVAS.h,
                   background: "#fff", border: "1px solid #d1d5db", margin: "0 auto", overflow: "hidden" }}
        >
          <DesignBlock
            block={{ ...webpage.pharmacyName, w: 60, h: 22 }}
            canvasSize={WEB_CANVAS}
            resizable={false}
            onChange={(b) => setWebpage((w) => ({ ...w, pharmacyName: { ...w.pharmacyName, x: b.x, y: b.y } }))}
            onSelect={() => {}}
            selected={false}
          >
            <div style={{
              width: "100%", height: "100%", display: "flex", alignItems: "center",
              justifyContent: webpage.pharmacyName.align === "left" ? "flex-start" : webpage.pharmacyName.align === "right" ? "flex-end" : "center",
              fontSize: webpage.pharmacyName.fontSize, color: webpage.pharmacyName.fg, fontWeight: 700
            }}>
              Pharmacy name
            </div>
          </DesignBlock>
        </div>

        <div style={{ marginTop: 12, fontSize: 12, color: "#9ca3af" }}>
          Everything below the name (rating, comments, complaint) is fixed underneath it, in that order.
        </div>
      </div>

      <div style={{ ...S.card, minWidth: 260, flex: 1 }}>
        <label style={S.label}>Alignment</label>
        <div style={{ display: "flex", gap: 6 }}>
          {["left", "center", "right"].map((a) => (
            <button key={a}
                    style={{ ...S.btnGhost, flex: 1, background: webpage.pharmacyName.align === a ? "#ede9fe" : "#fff",
                             borderColor: webpage.pharmacyName.align === a ? "#7c3aed" : "#d1d5db" }}
                    onClick={() => setWebpage((w) => ({ ...w, pharmacyName: { ...w.pharmacyName, align: a } }))}>
              {a[0].toUpperCase() + a.slice(1)}
            </button>
          ))}
        </div>

        <label style={S.label}>Font size</label>
        <input type="range" min="14" max="40" value={webpage.pharmacyName.fontSize}
               onChange={(e) => setWebpage((w) => ({ ...w, pharmacyName: { ...w.pharmacyName, fontSize: Number(e.target.value) } }))}
               style={{ width: "100%" }} />

        <ColorField label="Name colour" value={webpage.pharmacyName.fg}
                    onChange={(v) => setWebpage((w) => ({ ...w, pharmacyName: { ...w.pharmacyName, fg: v } }))} />

        <label style={S.label}>Intro text</label>
        <input style={S.input} value={webpage.introText}
               onChange={(e) => setWebpage((w) => ({ ...w, introText: e.target.value }))} />

        <div style={{ borderTop: "1px solid #e5e7eb", margin: "14px 0 10px" }} />
        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>Show on the form</div>
        {[
          ["showRating", "Satisfaction rating", "ratingHelp"],
          ["showComments", "Feedback / comments", "commentsHelp"],
          ["showComplaint", "Complaint option", "complaintHelp"]
        ].map(([k, l, helpKey]) => (
          <div key={k} style={{ marginBottom: 10 }}>
            <label style={{ fontSize: 13, color: "#374151", display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
              <input type="checkbox" checked={webpage[k]} onChange={(e) => setWebpage((w) => ({ ...w, [k]: e.target.checked }))} />
              {l}
            </label>
            {webpage[k] && (
              <input style={{ ...S.input, marginLeft: 24, width: "calc(100% - 24px)" }}
                     value={webpage[helpKey] || ""} placeholder="Explanation shown to the customer under this section…"
                     onChange={(e) => setWebpage((w) => ({ ...w, [helpKey]: e.target.value }))} />
            )}
          </div>
        ))}

        <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
          <button style={S.btn} onClick={onSave} disabled={saving}>{saving ? "Saving…" : "Save design"}</button>
          <button style={S.btnGhost} onClick={() => setShowPreview(true)}>Preview</button>
        </div>
        <div style={{ marginTop: 8 }}>
          <button style={S.btnGhost} onClick={restoreDefaults}>Restore defaults</button>
        </div>
        {message && <div style={message.type === "err" ? S.err : S.ok}>{message.text}</div>}
        {localMsg && <div style={localMsg.type === "err" ? S.err : S.ok}>{localMsg.text}</div>}
      </div>

      {showPreview && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", zIndex: 50,
                      display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
             onClick={() => setShowPreview(false)}>
          <div style={{ background: "#fff", borderRadius: 10, padding: 28, maxWidth: 480, width: "100%", maxHeight: "88vh", overflow: "auto" }}
               onClick={(e) => e.stopPropagation()}>
            <FeedbackFormPreview webpage={webpage} pharmacyName="Sample Pharmacy" interactive={false} />
            <button style={{ ...S.btnGhost, width: "100%", marginTop: 16 }} onClick={() => setShowPreview(false)}>Close preview</button>
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// BRANCHES TAB — grid of counts + Excel export
// =============================================================================
function todayISO() { return new Date().toISOString().slice(0, 10); }
function monthStartISO() { const d = new Date(); d.setDate(1); return d.toISOString().slice(0, 10); }
function yearStartISO() { const d = new Date(); d.setMonth(0, 1); return d.toISOString().slice(0, 10); }

function BranchesTab() {
  const [from, setFrom] = useState(monthStartISO());
  const [to, setTo] = useState(todayISO());
  const [rows, setRows] = useState(null);
  const [orgName, setOrgName] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(async (f, t) => {
    setLoading(true); setErr(null);
    try {
      const r = await apiPost("/api/feedback/summary", { from: f || undefined, to: t || undefined });
      setRows(r.branches);
      setOrgName(r.organisationName);
    } catch (e) { setErr(e.message); }
    setLoading(false);
  }, []);

  useEffect(() => { load(from, to); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Same shape as feedback_common.js's buildFeedbackWorkbookBuffer() on the
  // server, so a report emailed automatically and one exported by hand
  // here are the identical spreadsheet.
  const exportExcel = () => {
    if (!rows) return;
    const wb = XLSX.utils.book_new();
    const header = [
      ["Organisation", orgName],
      ["Date printed", todayISO()],
      ["Date range", `${from || "all time"} to ${to || todayISO()}`],
      [],
      ["Branch", "Total responses", "Average rating", "Rated responses", "Comments", "Complaints", "Complaints reviewed", "Awaiting review"]
    ];
    const body = rows.map(r => [
      r.pharmacyName, r.total, r.avgRating ?? "", r.ratingCount, r.commentsCount, r.complaints, r.complaintsReviewed, r.pending
    ]);
    const ws = XLSX.utils.aoa_to_sheet([...header, ...body]);
    XLSX.utils.book_append_sheet(wb, ws, "Patient feedback");
    XLSX.writeFile(wb, `patient_feedback_${from || "all"}_to_${to || todayISO()}.xlsx`);
  };

  return (
    <div>
      <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 14 }}>
        <div>
          <label style={S.label}>From</label>
          <input type="date" style={S.input} value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div>
          <label style={S.label}>To</label>
          <input type="date" style={S.input} value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <button style={S.btnGhost} onClick={() => load(from, to)}>Apply</button>
        <button style={S.btnGhost} onClick={() => { const f = monthStartISO(), t = todayISO(); setFrom(f); setTo(t); load(f, t); }}>This month</button>
        <button style={S.btnGhost} onClick={() => { const f = yearStartISO(), t = todayISO(); setFrom(f); setTo(t); load(f, t); }}>This year</button>
        <button style={S.btnGhost} onClick={() => { setFrom(""); setTo(""); load("", ""); }}>All time</button>
        <div style={{ flex: 1 }} />
        <button style={S.btn} onClick={exportExcel} disabled={!rows || loading}>Export to Excel</button>
      </div>

      {err && <div style={S.err}>{err}</div>}
      {loading && <div style={{ color: "#6b7280", fontSize: 13 }}>Loading…</div>}

      {rows && (
        <div style={{ ...S.card, overflowX: "auto" }}>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Branch</th>
                <th style={S.th}>Total</th>
                <th style={S.th}>Avg rating</th>
                <th style={S.th}>Comments</th>
                <th style={S.th}>Complaints</th>
                <th style={S.th}>Complaints reviewed</th>
                <th style={S.th}>Awaiting review</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.dbName}>
                  <td style={S.td}>{r.pharmacyName}</td>
                  <td style={S.td}>{r.total}</td>
                  <td style={S.td}>{r.avgRating != null ? `${r.avgRating} ★ (${r.ratingCount})` : "—"}</td>
                  <td style={S.td}>{r.commentsCount}</td>
                  <td style={{ ...S.td, color: r.complaints ? "#b91c1c" : undefined, fontWeight: r.complaints ? 700 : 400 }}>{r.complaints}</td>
                  <td style={S.td}>{r.complaintsReviewed}</td>
                  <td style={{ ...S.td, color: r.pending ? "#b45309" : undefined, fontWeight: r.pending ? 700 : 400 }}>{r.pending}</td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td style={S.td} colSpan={7}>No branches found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// INBOX TAB
// =============================================================================
function StarsReadout({ n }) {
  if (!n) return <span style={{ color: "#9ca3af" }}>—</span>;
  return <span style={{ color: "#f59e0b" }}>{"★".repeat(n)}{"☆".repeat(5 - n)}</span>;
}

function InboxRow({ entry, onSaved }) {
  const [note, setNote] = useState(entry.reviewNote || "");
  const [busy, setBusy] = useState(false);
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [replyBusy, setReplyBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const save = async (markReviewed) => {
    setBusy(true); setMsg(null);
    try {
      await apiPost("/api/feedback/inbox/review", { dbName: entry.dbName, id: entry._id, reviewNote: note, reviewed: markReviewed });
      onSaved();
    } catch (e) { setMsg(e.message); }
    setBusy(false);
  };

  const sendReply = async () => {
    setReplyBusy(true); setMsg(null);
    try {
      await apiPost("/api/feedback/inbox/reply", { dbName: entry.dbName, id: entry._id, message: replyText });
      setReplyOpen(false); setReplyText("");
      onSaved();
    } catch (e) { setMsg(e.message); }
    setReplyBusy(false);
  };

  return (
    <div style={{ ...S.card, marginBottom: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 14 }}>
            {entry.pharmacyName}
            {entry.isComplaint && (
              <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, color: "#991b1b",
                             background: "#fee2e2", border: "1px solid #fecaca", borderRadius: 4, padding: "2px 6px" }}>
                COMPLAINT
              </span>
            )}
            {entry.reviewed && (
              <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, color: "#166534",
                             background: "#dcfce7", border: "1px solid #86efac", borderRadius: 4, padding: "2px 6px" }}>
                REVIEWED
              </span>
            )}
          </div>
          <div style={{ fontSize: 12, color: "#6b7280" }}>{new Date(entry.submittedAt).toLocaleString()}</div>
        </div>
        <StarsReadout n={entry.rating} />
      </div>

      {entry.comments && <div style={{ fontSize: 13.5, color: "#374151", marginTop: 8 }}>{entry.comments}</div>}

      {(entry.customerName || entry.customerEmail) && (
        <div style={{ fontSize: 12, color: "#6b7280", marginTop: 6 }}>
          From: {entry.customerName || "—"} {entry.customerEmail ? `<${entry.customerEmail}>` : ""}
          {entry.replySentAt && <span style={{ marginLeft: 8, color: "#166534" }}>· replied {new Date(entry.replySentAt).toLocaleDateString()}</span>}
        </div>
      )}

      <label style={S.label}>Review note</label>
      <textarea style={{ ...S.textarea, minHeight: 44 }} value={note} onChange={(e) => setNote(e.target.value)} />

      <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
        <button style={S.btnGhost} onClick={() => save(false)} disabled={busy}>Save note</button>
        {!entry.reviewed && <button style={S.btn} onClick={() => save(true)} disabled={busy}>Mark reviewed</button>}
        {entry.reviewed && <button style={S.btnGhost} onClick={() => save(false)} disabled={busy}>Reopen</button>}
        {entry.customerEmail && (
          <button style={S.btnGhost} onClick={() => setReplyOpen((o) => !o)}>{replyOpen ? "Cancel reply" : "Reply by email"}</button>
        )}
      </div>

      {replyOpen && (
        <div style={{ marginTop: 10 }}>
          <textarea style={S.textarea} placeholder="Your reply…" value={replyText} onChange={(e) => setReplyText(e.target.value)} />
          <button style={{ ...S.btn, marginTop: 6 }} onClick={sendReply} disabled={replyBusy || !replyText.trim()}>
            {replyBusy ? "Sending…" : "Send reply"}
          </button>
          <div style={{ fontSize: 11.5, color: "#9ca3af", marginTop: 4 }}>
            Sent from the platform once — any further back-and-forth goes straight to the reply email you've configured, not through here.
          </div>
        </div>
      )}
      {msg && <div style={S.err}>{msg}</div>}
    </div>
  );
}

function InboxTab() {
  const [status, setStatus] = useState("pending"); // 'pending' | 'reviewed' | 'all'
  const [entries, setEntries] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(async (s) => {
    setLoading(true); setErr(null);
    try {
      const r = await apiPost("/api/feedback/inbox/list", { status: s });
      setEntries(r.entries);
    } catch (e) { setErr(e.message); }
    setLoading(false);
  }, []);

  useEffect(() => { load(status); }, [status, load]);

  return (
    <div>
      <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
        {[["pending", "Awaiting review"], ["reviewed", "Reviewed"], ["all", "All"]].map(([k, l]) => (
          <button key={k} style={{ ...S.btnGhost, background: status === k ? "#ede9fe" : "#fff", borderColor: status === k ? "#7c3aed" : "#d1d5db" }}
                  onClick={() => setStatus(k)}>
            {l}
          </button>
        ))}
      </div>
      {err && <div style={S.err}>{err}</div>}
      {loading && <div style={{ color: "#6b7280", fontSize: 13 }}>Loading…</div>}
      {entries && entries.length === 0 && <div style={{ color: "#6b7280", fontSize: 13 }}>Nothing here.</div>}
      {entries && entries.map((e) => (
        <InboxRow key={`${e.dbName}_${e._id}`} entry={e} onSaved={() => load(status)} />
      ))}
    </div>
  );
}

// =============================================================================
// LOG TAB — every submission, reply, report send, alert send and poster
// cascade send, newest first. Date range filter, same widget as Branches.
// Complements (doesn't replace) the per-entry replySentAt shown in Inbox.
// =============================================================================
const LOG_TYPE_STYLE = {
  "Submission": { bg: "#eef2ff", fg: "#3730a3" },
  "Submission (complaint)": { bg: "#fee2e2", fg: "#991b1b" },
  "Reply email": { bg: "#dcfce7", fg: "#166534" },
  "Report emailed": { bg: "#e0f2fe", fg: "#075985" },
  "Alert email": { bg: "#fef3c7", fg: "#92400e" },
  "Poster cascade email": { bg: "#f3e8ff", fg: "#6b21a8" }
};

function LogTab() {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [entries, setEntries] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(async (f, t) => {
    setLoading(true); setErr(null);
    try {
      const r = await apiPost("/api/feedback/log/list", { from: f || undefined, to: t || undefined });
      setEntries(r.entries);
    } catch (e) { setErr(e.message); }
    setLoading(false);
  }, []);

  useEffect(() => { load(from, to); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 14 }}>
        <div>
          <label style={S.label}>From</label>
          <input type="date" style={S.input} value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div>
          <label style={S.label}>To</label>
          <input type="date" style={S.input} value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <button style={S.btnGhost} onClick={() => load(from, to)}>Apply</button>
        <button style={S.btnGhost} onClick={() => { setFrom(""); setTo(""); load("", ""); }}>All time</button>
      </div>

      {err && <div style={S.err}>{err}</div>}
      {loading && <div style={{ color: "#6b7280", fontSize: 13 }}>Loading…</div>}

      {entries && (
        <div style={{ ...S.card, overflowX: "auto" }}>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Date</th>
                <th style={S.th}>Branch</th>
                <th style={S.th}>Event</th>
                <th style={S.th}>Recipient</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => {
                const tone = LOG_TYPE_STYLE[e.type] || { bg: "#f3f4f6", fg: "#374151" };
                return (
                  <tr key={e._id}>
                    <td style={S.td}>{new Date(e.date).toLocaleString()}</td>
                    <td style={S.td}>{e.branch || "—"}</td>
                    <td style={S.td}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: tone.fg, background: tone.bg,
                                     border: `1px solid ${tone.fg}22`, borderRadius: 4, padding: "2px 6px" }}>
                        {e.type}
                      </span>
                      {e.detail && <span style={{ marginLeft: 6, fontSize: 12, color: "#6b7280" }}>{e.detail}</span>}
                    </td>
                    <td style={S.td}>{e.recipient || "—"}</td>
                  </tr>
                );
              })}
              {entries.length === 0 && (
                <tr><td style={S.td} colSpan={4}>Nothing logged yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// CONFIG TAB WRAPPER — loads/saves org config, hosts poster + webpage tabs
// =============================================================================
function ConfigSection({ tab }) {
  const [poster, setPoster] = useState(null);
  const [webpage, setWebpage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const cfg = await apiPost("/api/feedback/config", {});
        setPoster(cfg.poster);
        setWebpage(cfg.webpage);
      } catch (e) { setErr(e.message); }
      setLoading(false);
    })();
  }, []);

  const save = async () => {
    setSaving(true); setMessage(null);
    try {
      await apiPost("/api/feedback/config/save", { poster, webpage });
      setMessage({ type: "ok", text: "Saved." });
    } catch (e) { setMessage({ type: "err", text: e.message }); }
    setSaving(false);
  };

  if (loading) return <div style={{ color: "#6b7280", fontSize: 13 }}>Loading…</div>;
  if (err) return <div style={S.err}>{err}</div>;

  return (
    <div>
      {tab === "poster" && <PosterDesignTab poster={poster} setPoster={setPoster} saving={saving} onSave={save} message={message} />}
      {tab === "webpage" && <WebpageDesignTab webpage={webpage} setWebpage={setWebpage} saving={saving} onSave={save} message={message} />}
    </div>
  );
}

// =============================================================================
// EMAILS TAB — who gets each branch's poster (moved here from Poster design)
// + the reply/alert address (moved here from Webpage design). One home for
// every email-related setting in this module.
// =============================================================================
function ReplyEmailCard() {
  const [replyEmail, setReplyEmail] = useState("");
  const [alertsEnabled, setAlertsEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const cfg = await apiPost("/api/feedback/config", {});
        setReplyEmail(cfg.replyEmail || "");
        setAlertsEnabled(cfg.alertsEnabled !== false);
      } catch (e) { setErr(e.message); }
      setLoading(false);
    })();
  }, []);

  const save = async () => {
    setSaving(true); setMessage(null);
    try {
      await apiPost("/api/feedback/config/save", { replyEmail, alertsEnabled });
      setMessage({ type: "ok", text: "Saved." });
    } catch (e) { setMessage({ type: "err", text: e.message }); }
    setSaving(false);
  };

  return (
    <div style={{ ...S.card, maxWidth: 420 }}>
      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8 }}>Replies &amp; daily alerts</div>
      {loading ? (
        <div style={{ color: "#6b7280", fontSize: 13 }}>Loading…</div>
      ) : (
        <>
          {err && <div style={S.err}>{err}</div>}
          <label style={S.label}>Reply email for this service</label>
          <input style={S.input} value={replyEmail} onChange={(e) => setReplyEmail(e.target.value)} placeholder="feedback@yourpharmacy.co.uk" />
          <div style={{ fontSize: 11.5, color: "#9ca3af", marginTop: 4 }}>
            Customer replies are routed here after the first message — future correspondence happens by ordinary
            email, not through the portal. This address also receives a daily alert whenever feedback is awaiting review.
          </div>
          <label style={{ fontSize: 13, color: "#374151", display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
            <input type="checkbox" checked={alertsEnabled} onChange={(e) => setAlertsEnabled(e.target.checked)} />
            Send the daily "awaiting review" alert
          </label>
          <button style={{ ...S.btn, marginTop: 10 }} onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</button>
          {message && <div style={message.type === "err" ? S.err : S.ok}>{message.text}</div>}
        </>
      )}
    </div>
  );
}

// Small shared frequency picker — monthly / quarterly / yearly — used by
// both report switches below. Fires on the 1st of the relevant period
// (see feedback_reports_runner.js: isReportDay / rangeForFrequency).
function FrequencyPicker({ value, onChange }) {
  return (
    <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
      {["monthly", "quarterly", "yearly"].map((f) => (
        <button key={f} type="button"
                style={{ ...S.btnGhost, flex: 1, padding: "6px 10px",
                         background: value === f ? "#ede9fe" : "#fff",
                         borderColor: value === f ? "#7c3aed" : "#d1d5db" }}
                onClick={() => onChange(f)}>
          {f[0].toUpperCase() + f.slice(1)}
        </button>
      ))}
    </div>
  );
}

const DEFAULT_REPORTS = {
  toBranches: { enabled: false, frequency: "monthly" },
  toEmails: { enabled: false, emails: "", frequency: "monthly" }
};

// =============================================================================
// SCHEDULED REPORTS CARD — the same Branches-tab grid/Excel, emailed
// automatically to branches and/or a fixed list of addresses.
// =============================================================================
function ReportsCard() {
  const [reports, setReports] = useState(DEFAULT_REPORTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const cfg = await apiPost("/api/feedback/config", {});
        setReports({ ...DEFAULT_REPORTS, ...(cfg.reports || {}) });
      } catch (e) { setErr(e.message); }
      setLoading(false);
    })();
  }, []);

  const save = async () => {
    setSaving(true); setMessage(null);
    try {
      await apiPost("/api/feedback/config/save", { reports });
      setMessage({ type: "ok", text: "Saved." });
    } catch (e) { setMessage({ type: "err", text: e.message }); }
    setSaving(false);
  };

  const setBranchesReport = (patch) => setReports((r) => ({ ...r, toBranches: { ...r.toBranches, ...patch } }));
  const setEmailsReport = (patch) => setReports((r) => ({ ...r, toEmails: { ...r.toEmails, ...patch } }));

  return (
    <div style={{ ...S.card, maxWidth: 460 }}>
      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8 }}>Scheduled reports</div>
      {loading ? (
        <div style={{ color: "#6b7280", fontSize: 13 }}>Loading…</div>
      ) : (
        <>
          {err && <div style={S.err}>{err}</div>}
          <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 12 }}>
            Both send the same report — organisation name, the period covered, and the grid of branch numbers
            (same as "Export to Excel" on the Branches tab) — as an Excel attachment.
          </div>

          <label style={{ fontSize: 13, color: "#374151", display: "flex", alignItems: "center", gap: 8 }}>
            <input type="checkbox" checked={reports.toBranches.enabled}
                   onChange={(e) => setBranchesReport({ enabled: e.target.checked })} />
            Send reports to branches
          </label>
          {reports.toBranches.enabled && (
            <div style={{ marginLeft: 24, marginBottom: 4 }}>
              <div style={{ fontSize: 11.5, color: "#9ca3af", marginTop: 2 }}>
                Goes to each branch's chosen recipient above.
              </div>
              <FrequencyPicker value={reports.toBranches.frequency} onChange={(f) => setBranchesReport({ frequency: f })} />
            </div>
          )}

          <div style={{ borderTop: "1px solid #e5e7eb", margin: "14px 0 10px" }} />

          <label style={{ fontSize: 13, color: "#374151", display: "flex", alignItems: "center", gap: 8 }}>
            <input type="checkbox" checked={reports.toEmails.enabled}
                   onChange={(e) => setEmailsReport({ enabled: e.target.checked })} />
            Send reports to specified email addresses
          </label>
          {reports.toEmails.enabled && (
            <div style={{ marginLeft: 24, marginTop: 6 }}>
              <label style={S.label}>Email addresses (comma separated)</label>
              <textarea style={{ ...S.textarea, minHeight: 70 }}
                        value={reports.toEmails.emails}
                        onChange={(e) => setEmailsReport({ emails: e.target.value })}
                        placeholder="area.manager@yourpharmacy.co.uk, superintendent@yourpharmacy.co.uk" />
              <FrequencyPicker value={reports.toEmails.frequency} onChange={(f) => setEmailsReport({ frequency: f })} />
            </div>
          )}

          <button style={{ ...S.btn, marginTop: 14 }} onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</button>
          {message && <div style={message.type === "err" ? S.err : S.ok}>{message.text}</div>}
        </>
      )}
    </div>
  );
}

function EmailsTab() {
  return (
    <div>
      <RecipientsPanel />
      <div style={{ marginTop: 16 }}>
        <ReplyEmailCard />
      </div>
      <div style={{ marginTop: 16 }}>
        <ReportsCard />
      </div>
    </div>
  );
}

const HOW_TO_STEPS = [
  { n: 1, text: "Click 'Emails' to set up who receives the flyers to put up in the pharmacy advertising the service, and who is to receive alerts when messages require a response.", tab: "emails", tabLabel: "Emails" },
  { n: 2, text: "Change the default design of the poster by clicking 'Poster design' and adjusting the font and colour of each section. Preview when required.", tab: "poster", tabLabel: "Poster design" },
  { n: 3, text: "Click 'Webpage design' to decide which elements you wish to have entered by the customer, and the font and colour scheme. Preview when required.", tab: "webpage", tabLabel: "Webpage design" },
  { n: 4, text: "Review and action incoming messages from 'Inbox', or see the numbers received by pharmacy in 'Branches'.", tab: "inbox", tabLabel: "Inbox" }
];

function HowToUsePanel({ onJump }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ fontSize: 12, fontWeight: 800, color: "#374151", textTransform: "uppercase",
                    letterSpacing: 0.5, marginBottom: 8 }}>
        How to use the feedback/complaints module
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 10 }}>
        {HOW_TO_STEPS.map((step) => (
          <div key={step.n}
               onClick={() => onJump(step.tab)}
               style={{ ...S.card, cursor: "pointer", borderColor: "#ede9fe", background: "#faf5ff" }}
               title={`Go to ${step.tabLabel}`}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <span style={{ width: 20, height: 20, borderRadius: "50%", background: "#7c3aed", color: "#fff",
                             fontSize: 11, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center",
                             flexShrink: 0 }}>
                {step.n}
              </span>
              <span style={{ fontSize: 12.5, fontWeight: 700, color: "#7c3aed" }}>{step.tabLabel}</span>
            </div>
            <div style={{ fontSize: 12, color: "#374151", lineHeight: 1.45 }}>{step.text}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// =============================================================================
// TOP-LEVEL EXPORT
// =============================================================================
export default function PatientFeedback() {
  const [tab, setTab] = useState("inbox"); // 'inbox' | 'branches' | 'emails' | 'webpage' | 'poster' | 'log'

  return (
    <div style={S.wrap}>
      <div style={S.h1}>Patient feedback</div>
      <div style={S.sub}>Design the wall poster and the webpage it links to, then review what comes back.</div>

      <HowToUsePanel onJump={setTab} />

      <div style={S.tabs}>
        <button style={S.tabBtn(tab === "inbox")} onClick={() => setTab("inbox")}>Inbox</button>
        <button style={S.tabBtn(tab === "branches")} onClick={() => setTab("branches")}>Branches</button>
        <div style={{ flex: 1 }} />
        <button style={S.tabBtn(tab === "emails")} onClick={() => setTab("emails")}>Emails</button>
        <button style={S.tabBtn(tab === "webpage")} onClick={() => setTab("webpage")}>Webpage design</button>
        <button style={S.tabBtn(tab === "poster")} onClick={() => setTab("poster")}>Poster design</button>
        <button style={S.tabBtn(tab === "log")} onClick={() => setTab("log")}>Log</button>
      </div>

      {tab === "inbox" && <InboxTab />}
      {tab === "branches" && <BranchesTab />}
      {tab === "emails" && <EmailsTab />}
      {(tab === "poster" || tab === "webpage") && <ConfigSection tab={tab} />}
      {tab === "log" && <LogTab />}
    </div>
  );
}

export { FeedbackFormPreview };
